<script>
  import { onMount } from 'svelte';
  import { currentView, locate } from './router.js';
  import Loader from '../components/Loader.svelte';

  const meta = (name) => document.querySelector(`meta[name="${name}"]`)?.content || '';

  const isIn1     = meta('auth-website-in1') === 'true';
  const siteName  = meta('auth-website-name');
  const logoUrl   = meta('auth-website-logo');

  let loaded = $state(false);
  let view = $state(null);

  $effect(() => {
    return currentView.subscribe(v => { view = v; });
  });

  onMount(async () => {
    await locate();
    loaded = true;
  });
</script>

<div class="auth">
  <div class="auth__window">
    <a href="/" class="auth__logo">
      {#if isIn1}
        <img src="/img/in1.svg" alt="in1">
      {:else if logoUrl}
        <img src={logoUrl} alt={siteName}>
      {:else}
        {siteName}
      {/if}
    </a>

    {#if loaded && view}
      {#key view}
        <svelte:component this={view} />
      {/key}
    {:else}
      <div class="auth__loader">
        <Loader size={36} color="var(--primary-color, #0a1542)" />
      </div>
    {/if}
  </div>
</div>

<svelte:window onpopstate={locate} />

<style>
  /* Layout */
  :global(*, *::before, *::after) { box-sizing: border-box; }

  .auth {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: var(--background-color, #e9e9e9);
    font-family: var(--font-default, Roboto, sans-serif);
    font-size: var(--text-size-default, 16px);
    color: var(--text-color, #1a1a1a);
    padding: var(--spacing, 1rem);
  }

  .auth__window {
    width: 100%;
    max-width: 380px;
    background: var(--auth-window-bg, #fff);
    border-radius: var(--auth-border-radius, 8px);
    box-shadow: var(--auth-shadow, 0 15px 50px rgba(0, 0, 0, 0.15));
    padding: 2.5rem 2rem;
  }

  .auth__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2.5rem;
    text-decoration: none;
    color: var(--text-color, #1a1a1a);
    font-weight: 600;
    font-size: 1.25rem;
  }

  .auth__logo img {
    max-height: 80px;
    max-width: 60%;
    width: auto;
  }

  .auth__loader {
    display: flex;
    justify-content: center;
    padding: 2rem 0;
  }

  @media (max-width: 480px) {
    .auth { align-items: flex-start; padding: 0; }
    .auth__window {
      max-width: 100%;
      min-height: 100vh;
      border-radius: 0;
      box-shadow: none;
    }
  }

  /* Shared BEM classes — defined globally so form components can use them
     without Svelte scoping, and merchants can override via their layout CSS. */
  :global(.auth__form) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  :global(.auth__field) {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  :global(.auth__label) {
    font-size: 0.82rem;
    color: var(--text-color, #1a1a1a);
    opacity: 0.8;
  }

  :global(.auth__input) {
    width: 100%;
    padding: 0.6rem 0.6rem;
    border: var(--auth-input-border, 1px solid var(--secondary-color, #5e6cc2));
    border-radius: calc(var(--auth-border-radius, 8px) / 2);
    font: inherit;
    font-size: var(--text-size-default, 16px);
    background: transparent;
    color: var(--text-color, #1a1a1a);
    transition: border-color 0.15s;
  }

  :global(.auth__input:focus) {
    outline: none;
    border-color: var(--primary-color, #0a1542);
  }

  :global(.auth__input:disabled) { opacity: 0.55; }

  :global(.auth__checkbox-row) {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.88rem;
    line-height: 1.4;
  }

  :global(.auth__checkbox-row input[type="checkbox"]) {
    margin-top: 0.15rem;
    flex-shrink: 0;
    accent-color: var(--primary-color, #0a1542);
  }

  :global(.auth__checkbox-row a) {
    color: var(--primary-color, #0a1542);
  }

  :global(.auth__button) {
    width: 100%;
    padding: 0.75rem;
    margin-top: 1.5rem;
    background: var(--primary-color, #0a1542);
    color: #fff;
    border: none;
    border-radius: var(--auth-border-radius, 8px);
    font: inherit;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2.75rem;
    transition: opacity 0.15s;
  }

  :global(.auth__button:disabled) { opacity: 0.6; cursor: default; }

  :global(.auth__alert) {
    padding: 0.75rem 0.75rem;
    border-radius: calc(var(--auth-border-radius, 8px) / 2);
    font-size: 0.88rem;
    line-height: 1.4;
    list-style: none;
  }

  :global(.auth__alert li) {
    margin-bottom: 0.75rem;
    line-height: 1.1;
  }

  :global(.auth__alert li:last-child) {
    margin-bottom: 0;
  }

  :global(.auth__alert--danger)  { background: #fde8e8; color: #b91c1c; }
  :global(.auth__alert--success) { background: #e8f6ee; color: #166534; }

  :global(.auth__error) {
    font-size: 0.78rem;
    color: #b91c1c;
    margin-top: 0.1rem;
  }

  :global(.auth__links) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.07);
  }

  :global(.auth__link) {
    background: none;
    border: none;
    color: var(--primary-color, #0a1542);
    font: inherit;
    font-size: 0.88rem;
    cursor: pointer;
    text-decoration: none;
    padding: 0;
    opacity: 0.8;
  }

  :global(.auth__link:hover) { opacity: 1; }
</style>
